import { FormEvent, useContext, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { navigateWithParams } from '@creditasmx-utilities/helpers';

import { useMask } from '../../../shared/hooks/useMask';
import { INPUT_NAMES, errorExpressions } from './Form.constants';
import { FormValues, SendLoanInfoPayload } from './Form.types';
import { LocalStorageKeys } from '../../constants';
import {
  sendLoanInfo,
  sendTagExperiment,
} from '../../requests/externalAutoForm.api';
import { useTracking } from '../../../shared/utilities/tagManager/tagManager.hook';
import {
  TRACKING,
  TrackingCategory,
  TrackingEventName,
  TrackingEventsMap,
} from '../../tracking/tracking.constants';
import { AxiosInstance } from 'axios';

import { ExternalAutoFormContext } from '../../pages/mfExternalAutoForm.context';
import { useValidator } from '../../../shared/hooks/useValidator';
const INPUT_NAMES_KEYS = Object.keys(INPUT_NAMES);
const EMPTY_FORM_VALUES: FormValues = INPUT_NAMES_KEYS.reduce(
  (acc, inputName) => ({ ...acc, [inputName]: '' }),
  {},
);

export const fetchData = async (
  instance: AxiosInstance,
  payload?: any,
): Promise<any> => {
  const res = await instance.request({ data: payload });
  return res.data;
};

export const useForm = (externalAutoId: string) => {
  
  const { trackEvent } = useTracking();
  
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM_VALUES);
  
  const { errors, checkErrors } = useValidator({
    errorExpressions,
    inputNames: INPUT_NAMES,
  });
  
  const {
    goTo,
    autoInfoState: [autoInfo],
  } = useContext(ExternalAutoFormContext);
  const errorsContext = {
    [INPUT_NAMES.DOWN_PAYMENT]: autoInfo.price,
  };
  const { mutateAsync: mutateSendLoanInfo, isLoading } = useMutation(
    async (data: SendLoanInfoPayload) =>
      (await sendLoanInfo().request({ data })).data,
  );

  const postTagExperimentTesting = useQuery(
    'sendTagExperiment',
    () => fetchData(sendTagExperiment(), { tag: 'PUBLIC_V2_AB_TEST_MAIL' }),
    {
      enabled: false,
    },
  );

  const trackFormEvent = (eventName: string, value = ''): void => {
    const valueInLabel =
      eventName === 'PURCHASE_INTENT' ? { label: value } : {};

    trackEvent({
      event: {
        event: TrackingEventName.EXTERNAL_AUTO_PAGE,
        category: TrackingCategory.EXTERNAL_AUTO_PAGE,
        ...TrackingEventsMap[eventName],
        ...valueInLabel,
        value: value,
      },
    });
  };
  const setFormValue = (inputName: string, value: string): void => {
    setFormValues({ ...formValues, [inputName]: value });
    if (errors[inputName]) {
      checkErrors({ [inputName]: value }, errorsContext);
    }
  };

  const handleChangeInput = (event: FormEvent<HTMLInputElement>): void => {
    const { name, value } = event.currentTarget;
    setFormValue(name, value);
  };

  const handleBlurInput = (event: FormEvent<HTMLInputElement>): void => {
    const { name, value } = event.currentTarget;
    checkErrors({ [name]: value }, errorsContext);
    trackFormEvent(name, value);
  };

  const handleChangeDrop = (name: string, value: string): void => {
    setFormValue(name, value);
    trackFormEvent(name, value);
  };

  const handleContinue = async (): Promise<void> => {
    const payload: SendLoanInfoPayload = {
      downPayment: +formValues[INPUT_NAMES.DOWN_PAYMENT],
      term: +formValues[INPUT_NAMES.TERM],
      phone: +formValues[INPUT_NAMES.PHONE],
      purchaseIntention: formValues[INPUT_NAMES.PURCHASE_INTENT],
    };

    if (!checkErrors(formValues, errorsContext, true)) {
      await mutateSendLoanInfo(payload, {
        onSuccess: async () => {
          localStorage.setItem(
            LocalStorageKeys.EXTERNAL_AUTO_ID,
            externalAutoId,
          );
          trackFormEvent(TRACKING.EVENTS.SUBMIT);
          navigateWithParams(goTo.toPersonalInfo);
        },
      });
    }
  };

  const {
    maskedValue: downPayment,
    onChange: onChangeDownPayment,
    onBlur: onBlurDownPayment,
  } = useMask({
    value: formValues[INPUT_NAMES.DOWN_PAYMENT],
    onChange: handleChangeInput,
    onBlur: handleBlurInput,
    opts: { type: 'currency' },
  });
  
  const {
    maskedValue: phone,
    onChange: onChangePhone,
    onBlur: onBlurPhone,
  } = useMask({
    value: formValues[INPUT_NAMES.PHONE],
    onChange: handleChangeInput,
    onBlur: handleBlurInput,
    opts: {
      type: 'phone',
      prefix: '+52 ',
      length: 10,
    },
  });

  return {
    errors,
    isLoading,
    downPayment,
    onChangeDownPayment,
    onBlurDownPayment,
    phone,
    onChangePhone,
    onBlurPhone,
    handleChangeInput,
    handleBlurInput,
    handleContinue,
    handleChangeDrop,
  };
};
